import React, { useEffect, useState } from "react";
import "./AddTopic.scss";
import "../../../styles/main.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FormInput from "../../FormInputs/FormInput";
import { Button, Form, message } from "antd";
import { FormRadioGroup } from "../../FormInputs/FormRadio";
import {
  advertisementTiles,
  aspectRatiosDesktop,
  aspectRatiosMobile,
  tileCountToAspectRatiosDesktop,
  tileCountToAspectRatiosMobile,
  topicTypes,
} from "../../../services/customData";
import { FormCheckbox } from "../../FormInputs/FormCheckbox";
import { postApi } from "../../../utils/apiServices";
import { ADD_TOPIC, UPDATE_TOPIC } from "../../../utils/apiPath";

const AddTopic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  const [selectedDesktop, setSelectedDesktop] = useState(null);
  const [selectedMobile, setSelectedMobile] = useState(null);
  const [errors, setError] = useState({
    desktop: false,
    mobile: false,
  });
  const [topicType, setTopicType] = useState(null);
  const [sameTile, setSameTile] = useState(null);
  const [desktopTileCount, setDesktopTileCount] = useState(null);
  const [mobileTileCount, setMobileTileCount] = useState(null);

  useEffect(() => {
    if (id) {
      const topicData = location?.state?.topicData;
      form.setFieldsValue({
        title: topicData?.title,
        description: topicData?.description,
        metaTitle: topicData?.metaTitle,
        metaDescription: topicData?.metaDescription,
        urlName: topicData?.urlName,
        topicType: topicData?.topicType,
        isSlider: true,
        desktopTileCount: topicData?.desktopTileCount,
        mobileTileCount: topicData?.mobileTileCount,
        sameTile:
          topicData?.desktopAspectRatio === topicData?.mobileAspectRatio,
      });
      setDesktopTileCount(topicData?.desktopTileCount);
      setMobileTileCount(topicData?.mobileTileCount);
      setTopicType(topicData?.topicType);
      setSelectedDesktop(topicData?.desktopAspectRatio);
      setSelectedMobile(topicData?.mobileAspectRatio);
      if (topicData?.desktopAspectRatio === topicData?.mobileAspectRatio) {
        setSameTile(true);
      }
    }
  }, [id]);

  const handleValuesChange = (changedValues, allValues) => {
    if ("topicType" in changedValues) {
      setTopicType(changedValues.topicType);
      if (changedValues.topicType !== 1) {
        setSelectedDesktop(null);
        setSelectedMobile(null);
        form.setFieldsValue({
          advertisementId: null,
          sameTile: null,
        });
      }
    }
    if ("sameTile" in changedValues) {
      setSameTile(changedValues.sameTile);
      setSelectedMobile(null);
    }
    if ("mobileTileCount" in changedValues) {
      setMobileTileCount(changedValues.mobileTileCount);
      setSelectedMobile(null);
      form.setFieldsValue({
        sameTile: null,
      });
    }
    if ("desktopTileCount" in changedValues) {
      setDesktopTileCount(changedValues.desktopTileCount);
      setSelectedDesktop(null);
      form.setFieldsValue({
        sameTile: null,
      });
    }
  };

  const renderBoxes = (ratios, selected, setSelected) =>
    ratios?.map(({ label }) => (
      <div
        key={label}
        className={`add-topic_form_ratio_box ${
          selected === label ? "selected" : ""
        }`}
        data-ratio={label}
        onClick={() => setSelected(label)}
      >
        <span>{label}</span>
      </div>
    ));

  const handleBack = () => {
    navigate("/manage-topics");
  };

  const handleSubmit = async () => {
    try {
      const modifiedRules = {
        ...form.getFieldsValue(),
      };
      if (!selectedDesktop) {
        setError((prev) => ({ ...prev, desktop: true }));
      } else {
        setError((prev) => ({ ...prev, desktop: false }));
      }
      if (!selectedMobile) {
        setError((prev) => ({ ...prev, mobile: true }));
      } else {
        setError((prev) => ({ ...prev, mobile: false }));
      }
      await form.setFieldsValue(modifiedRules);
      await form
        .validateFields()
        .then((values) => {
          if (selectedDesktop && selectedMobile) {
            handleAdd(values);
          }
        })
        .catch((errorInfo) => {
          console.log(errorInfo);
        });
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleAdd = async (values) => {
    try {
      const payload = {
        topicId: id ? id : undefined,
        title: values?.title,
        description: "",
        metaTitle: "",
        metaDescription: "",
        urlName: "",
        topicType: values?.topicType,
        isSlider: values.isSlider || false,
        desktopTileCount: values?.desktopTileCount,
        mobileTileCount: values?.mobileTileCount,
        desktopAspectRatio: selectedDesktop,
        mobileAspectRatio: values?.sameTile ? selectedDesktop : selectedMobile,
      };
      setLoading(true);
      const url = id ? UPDATE_TOPIC : ADD_TOPIC;
      const res = await postApi(`${url}`, payload);
      setLoading(false);
      message.success(`Topic ${id ? "updated" : "added"} successfully`);
      navigate("/manage-topics");
      return res?.data || [];
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const filterAspectRatios = (ratios, validLabels) =>
    ratios?.filter(({ label }) => validLabels.includes(label));

  const validAspectRatiosDesktop =
    tileCountToAspectRatiosDesktop[desktopTileCount] || [];
  const validAspectRatiosMobile =
    tileCountToAspectRatiosMobile[desktopTileCount] || [];
  const filteredDesktopRatios = filterAspectRatios(
    aspectRatiosDesktop,
    validAspectRatiosDesktop
  );
  const filteredMobileRatios = filterAspectRatios(
    aspectRatiosMobile,
    validAspectRatiosMobile
  );

  return (
    <div className="add-topic">
      <div className="add-topic_container">
        <div className="add-topic_header">
          <h2>{id ? "Edit" : "Add"} Topic</h2>
          <div className="add-topic_header_links">
            <span onClick={handleBack}>Topics</span>
            <span className="active"> / {id ? "Edit" : "Add"}</span>
          </div>
        </div>
        <div className="add-topic_section">
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
          >
            <div className="add-topic_form">
              <FormInput
                label="Title"
                name="title"
                rules={[{ required: true, message: "Please enter title" }]}
                placeholder="Enter topic title"
              />
              <div className="add-topic_form_radio">
                <FormRadioGroup
                  label="Select topic type"
                  name="topicType"
                  rules={[
                    { required: true, message: "Please select an topic type" },
                  ]}
                  options={topicTypes}
                  disabled={!!id}
                />
              </div>
              {/* <FormCheckbox label="Slider is required" name="isSlider" /> */}
              {topicType === 1 && (
                <>
                  <div className="add-topic_form_radio">
                    <FormRadioGroup
                      label="Select advertisement desktop tile count"
                      name="desktopTileCount"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select an advertisement desktop tile count",
                        },
                      ]}
                      options={advertisementTiles}
                    />
                  </div>
                  {desktopTileCount && (
                    <div className="add-topic_form_ratio">
                      <h3>
                        <p>*</p> Select Advertisement Tile Ratio for Desktop
                      </h3>
                      <div className="add-topic_form_ratio_box-container">
                        {renderBoxes(
                          filteredDesktopRatios,
                          selectedDesktop,
                          setSelectedDesktop
                        )}
                      </div>
                      {errors?.desktop && (
                        <h6>
                          Please select advertisement tile ratio for desktop
                        </h6>
                      )}
                    </div>
                  )}
                  {/* {selectedDesktop && (
                    <FormCheckbox
                      label="Keep same tiles as advertisement for mobile as desktop"
                      name="sameTile"
                    />
                  )} */}
                  {/* {sameTile !== true && (
                    <div className="add-topic_form_radio">
                      <FormRadioGroup
                        label="Select advertisement mobile tile count"
                        name="mobileTileCount"
                        rules={[
                          {
                            required: true,
                            message:
                              "Please select an advertisement mobile tile count",
                          },
                        ]}
                        options={advertisementTiles}
                      />
                    </div>
                  )} */}
                  {desktopTileCount && (
                    <>
                      {sameTile !== true && (
                        <div className="add-topic_form_ratio">
                          <h3>
                            <p>*</p> Select select advertisement tile ratio for
                            mobile
                          </h3>
                          <div className="add-topic_form_ratio_box-container">
                            {renderBoxes(
                              filteredMobileRatios,
                              selectedMobile,
                              setSelectedMobile
                            )}
                          </div>
                          {errors?.mobile && (
                            <h6>
                              Please select advertisement tile ratio for mobile
                            </h6>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="add-topic_form_button">
              <Button loading={loading} type="primary" onClick={handleSubmit}>
                {id ? "Update" : "Create"} topic
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default AddTopic;
